import React, { useEffect, useState } from 'react';
import Logo from '../components/Logo';
import PublicNav from '../components/PublicNav';
import MobileCtaBar from '../components/MobileCtaBar';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Seo } from '../components/Seo';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import {
  Check, FileText, Sparkles, Camera, ShieldCheck, Lock, LayoutDashboard,
  Upload, ArrowRight, AlertTriangle, ClipboardCheck, Receipt, Banknote, Search, Mail
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FALLBACK_PACKS = [
  { id: 'basic_25', name: 'Basic Pack', credits: 25, price_display: 'A$2.99' },
  { id: 'standard_75', name: 'Standard Pack', credits: 75, price_display: 'A$6.99', popular: true },
  { id: 'premium_200', name: 'Premium Pack', credits: 200, price_display: 'A$14.99' },
  { id: 'multi_property', name: 'Multi-Property Unlock', credits: 75, price_display: 'A$4.99' },
];

const PACK_ORDER = ['basic_25', 'standard_75', 'premium_200', 'multi_property'];

const CLAIM_STEPS = [
  {
    icon: ClipboardCheck,
    title: '1. Start a claim session',
    body: 'Tell us the loss event — fire, flood, storm, burglary — plus the date and which rooms were affected. We set up a workspace just for that event.',
  },
  {
    icon: Camera,
    title: '2. Capture what is left',
    body: 'Photograph the damage room by room, straight from your phone. If you are standing in a burnt-out house with no signal, the capture queue holds everything and uploads when you are back online.',
  },
  {
    icon: Search,
    title: '3. Jog your memory, room by room',
    body: 'Guided prompts walk you through the things people always forget — the contents of drawers, the linen cupboard, the garage shelf, the kids\u2019 wardrobes — so the schedule is complete, not just the obvious big-ticket items.',
  },
  {
    icon: Receipt,
    title: '4. Attach your evidence',
    body: 'Upload any pre-loss photos you already have, receipts, and bank or card statement PDFs. We read them and match transactions to the items you are claiming, so each line has something behind it.',
  },
  {
    icon: ShieldCheck,
    title: '5. Run the assessor consistency checks',
    body: 'Before your insurer does. We flag the things an assessor looks for — quantities that look high for the home, premium claims sitting next to budget survivors, dates that do not line up — so you can fix or explain them first.',
  },
  {
    icon: FileText,
    title: '6. Export your Claim Assessment Report',
    body: 'A photo-rich report and schedule of loss, pre-sorted by settlement pathway, with the evidence attached and every assumption stated. Download it as PDF or CSV and send it to your claims team.',
  },
];

const Pricing = () => {
  const { isAuthenticated } = useAuth();
  const [packs, setPacks] = useState(FALLBACK_PACKS);

  useEffect(() => {
    axios
      .get(`${API_URL}/api/payments/packs`)
      .then((res) => {
        const list = (res.data?.packs || []).filter((p) => PACK_ORDER.includes(p.id));
        if (list.length) {
          list.sort((a, b) => PACK_ORDER.indexOf(a.id) - PACK_ORDER.indexOf(b.id));
          setPacks(list);
        }
      })
      .catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="pricing-page">
      <Seo
        title="Pricing — Free Cover Letter, Pay-As-You-Go AI | VerifAEye"
        description="No subscriptions. Your Inventory Supporting Cover Letter to your insurer is free. AI analysis credit packs from A$2.99, and a one-off post-loss AI Claim Assessment Report from A$19.95."
        path="/pricing"
      />

      <PublicNav isAuthenticated={isAuthenticated} current="/pricing" />
      <main id="main">

      {/* Hero */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8" style={{ background: 'linear-gradient(135deg, #F8FAFC 0%, #E2E8F0 100%)' }}>
        <div className="max-w-4xl mx-auto">
          <p className="text-blue-600 text-sm font-semibold uppercase tracking-wide">Pricing</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold text-slate-900 mt-4 tracking-tight">
            No subscriptions. Pay only when you use the AI.
          </h1>
          <p className="mt-6 text-base md:text-lg text-slate-600 max-w-2xl">
            Your contents inventory and the supporting cover letter you send your insurer are free.
            You only pay for AI analysis credits — and, if the worst happens and you need a full
            post-loss claim built from scratch, for that one claim.
          </p>
        </div>
      </section>

      {/* Free letter vs paid assessment */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-base md:text-lg font-semibold text-slate-900 uppercase tracking-wide">
            The important distinction
          </h2>
          <p className="mt-3 text-slate-600 max-w-2xl">
            Two different documents, two different jobs. One is free. One is not. Here is exactly
            which is which.
          </p>

          <div className="grid md:grid-cols-2 gap-6 mt-10">
            <div className="rounded-2xl border-2 border-emerald-200 bg-emerald-50/40 p-6 md:p-8" data-testid="free-letter-card">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-600 flex items-center justify-center">
                  <Mail className="w-5 h-5 text-white" />
                </div>
                <span className="text-xs font-semibold uppercase tracking-wider text-emerald-700">Free</span>
              </div>
              <h3 className="mt-5 text-xl font-semibold text-slate-900">Inventory Supporting Cover Letter</h3>
              <p className="mt-1 text-sm text-emerald-700 font-medium">Before a loss — the easy way</p>
              <p className="mt-4 text-sm text-slate-600 leading-relaxed">
                A covering letter you send to your insurer <em>along with</em> your inventory, to help
                support a swift claim settlement. Your items, pre-sorted by likely settlement pathway,
                with photo evidence and EXIF provenance attached.
              </p>
              <ul className="mt-6 space-y-2.5 text-sm text-slate-700">
                {[
                  'Covering letter addressed to your claims team',
                  'Full contents inventory with rooms, categories and values',
                  'Photo evidence with capture date and location',
                  'Email it straight to your insurer with your claim number in the subject line',
                  'Add your own paragraph — clearly marked as your words',
                  'Download as PDF, export to CSV',
                ].map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-2xl font-semibold text-slate-900">A$0</p>
              <p className="text-xs text-slate-500">Generating, exporting and emailing the cover letter is free.</p>
            </div>

            <div className="rounded-2xl border-2 border-amber-200 bg-amber-50/40 p-6 md:p-8" data-testid="paid-assessment-card">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-600 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-white" />
                </div>
                <span className="text-xs font-semibold uppercase tracking-wider text-amber-700">Paid, one-off</span>
              </div>
              <h3 className="mt-5 text-xl font-semibold text-slate-900">AI Claim Assessment Report</h3>
              <p className="mt-1 text-sm text-amber-700 font-medium">After a loss — the complex way</p>
              <p className="mt-4 text-sm text-slate-600 leading-relaxed">
                For when there was no inventory and you have to prove a claim from a burnt-out or
                flooded house. We help you reconstruct the whole thing, test it the way an assessor
                would, and produce a full report for one specific loss event.
              </p>
              <ul className="mt-6 space-y-2.5 text-sm text-slate-700">
                {[
                  'Guided room-by-room memory joggers so nothing is missed',
                  'Offline-capable damage photo capture',
                  'Bank and card statement PDF ingestion, matched to claim lines',
                  'Receipt and pre-loss photo evidence matching',
                  'Assessor consistency checks run before your insurer runs them',
                  'Photo-rich claim assessment report and schedule of loss (PDF + CSV)',
                ].map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-2xl font-semibold text-slate-900">
                A$24.95 <span className="text-sm font-normal text-slate-500">one-off, per loss event</span>
              </p>
              <p className="text-xs text-slate-500">
                A$19.95 if you have ever bought an analysis pack. Includes AI analysis for up to 300
                photos; a further 300 photos is A$9.95.
              </p>
            </div>
          </div>

          <div className="mt-8 rounded-xl border border-blue-200 bg-blue-50 p-5 flex items-start gap-3" data-testid="before-after-nudge">
            <AlertTriangle className="w-5 h-5 text-blue-700 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-blue-900 leading-relaxed">
              <strong>Do it now, not later.</strong> Building your inventory before anything happens
              takes minutes a room, and the supporting cover letter to your insurer is free. Rebuilding
              an inventory <em>after</em> a loss — from memory, from statements, from a house that is no
              longer there — is a far more complex job, which is why it costs more. We can still help
              you. It is just the harder, more expensive path.
            </p>
          </div>
        </div>
      </section>

      {/* Start free */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-base md:text-lg font-semibold text-slate-900 uppercase tracking-wide">
            Start free, then top up
          </h2>
          <p className="mt-3 text-slate-600 max-w-2xl">
            Every account starts with 5 free AI item analyses. After that, buy credits when you need
            them. Credits never expire and there is nothing to cancel.
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-10">
            <div className="rounded-2xl border border-slate-900 bg-slate-900 p-6 text-white" data-testid="pack-card-free">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-teal-300" />
                <span className="text-xs font-semibold uppercase tracking-wider text-teal-300">Free to start</span>
              </div>
              <h3 className="mt-4 text-lg font-semibold">5 AI analyses</h3>
              <p className="mt-2 text-3xl font-semibold">A$0</p>
              <p className="mt-3 text-sm text-slate-300">
                Verify your email and your first 5 item analyses are on us. No card required. The
                supporting cover letter is free too.
              </p>
              <Link to="/register" className="block mt-6">
                <Button className="w-full bg-white text-slate-900 hover:bg-slate-100" data-testid="pricing-start-free-btn">
                  Start free
                </Button>
              </Link>
            </div>

            {packs.map((pack) => (
              <div
                key={pack.id}
                className={`rounded-2xl border bg-white p-6 ${pack.popular ? 'border-blue-500 ring-1 ring-blue-500' : 'border-slate-200'}`}
                data-testid={`pack-card-${pack.id}`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    {pack.id === 'multi_property' ? 'Add a property' : 'Credit pack'}
                  </span>
                  {pack.popular && (
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">
                      Popular
                    </span>
                  )}
                </div>
                <h3 className="mt-4 text-lg font-semibold text-slate-900">{pack.name}</h3>
                <p className="mt-2 text-3xl font-semibold text-slate-900">{pack.price_display}</p>
                <p className="mt-3 text-sm text-slate-600">
                  {pack.id === 'multi_property'
                    ? `Unlock an additional property and get ${pack.credits} AI analyses for it.`
                    : `${pack.credits} AI item analyses. Works out at about A$${(
                        (parseFloat(String(pack.price_display).replace(/[^0-9.]/g, '')) / pack.credits) || 0
                      ).toFixed(2)} an item.`}
                </p>
                <Link to={isAuthenticated ? '/settings' : '/register'} className="block mt-6">
                  <Button variant="outline" className="w-full" data-testid={`pack-buy-${pack.id}`}>
                    {isAuthenticated ? 'Buy in Settings' : 'Create account'}
                  </Button>
                </Link>
              </div>
            ))}
          </div>

          <p className="mt-6 text-xs text-slate-500 max-w-3xl">
            All prices in Australian dollars and include GST where applicable. Payments are processed
            by Stripe. One analysis = one AI pass over one item photo. Your high-resolution photos stay
            in your own Google Drive.
          </p>
        </div>
      </section>

      {/* How a post-loss claim works */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-base md:text-lg font-semibold text-slate-900 uppercase tracking-wide">
            What the paid claim assessment actually does
          </h2>
          <p className="mt-3 text-slate-600 max-w-2xl">
            It is hard to picture how you would ever prove a whole houseful of contents after a fire.
            Here is the exact path we walk you down, step by step.
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
            {CLAIM_STEPS.map((step, i) => (
              <div
                key={step.title}
                className="rounded-2xl border border-slate-200 p-6 hover:border-slate-300 hover:shadow-sm transition-all duration-200"
                style={{ animationDelay: `${i * 60}ms` }}
                data-testid={`claim-step-${i + 1}`}
              >
                <div className="w-10 h-10 rounded-lg bg-slate-900 flex items-center justify-center">
                  <step.icon className="w-5 h-5 text-white" />
                </div>
                <h3 className="mt-5 text-base font-semibold text-slate-900">{step.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{step.body}</p>
              </div>
            ))}
          </div>

          <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <Link to={isAuthenticated ? '/claims/new' : '/register'}>
              <Button className="bg-slate-900 text-white hover:bg-slate-800" data-testid="pricing-claim-cta">
                {isAuthenticated ? 'Start a claim session' : 'Create your account'}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <p className="text-sm text-slate-500">
              Setting up rooms and reading the capture guide is free — you only pay when you are ready
              to run the AI over your photos.
            </p>
          </div>
        </div>
      </section>

      {/* Trust strip */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-900">
        <div className="max-w-6xl mx-auto grid md:grid-cols-3 gap-8">
          {[
            { icon: Lock, title: 'Your Drive, not ours', body: 'High-resolution photos are stored in your own Google Drive. We never hold your evidence hostage.' },
            { icon: Banknote, title: 'No subscriptions', body: 'Nothing recurring, nothing to cancel. Buy credits only when you need them.' },
            { icon: ShieldCheck, title: 'Not a loss assessor', body: 'VerifAEye documents and organises your evidence. We do not determine liability, entitlement or settlement value — your insurer does.' },
          ].map((t) => (
            <div key={t.title}>
              <t.icon className="w-5 h-5 text-teal-300" />
              <h3 className="mt-4 text-base md:text-lg font-semibold text-white">{t.title}</h3>
              <p className="mt-2 text-sm text-slate-300 leading-relaxed">{t.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Footer */}
      </main>

      <footer className="py-8 px-4 sm:px-6 lg:px-8 bg-white border-t border-slate-200 pb-24 sm:pb-8">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-2">
            <Logo size="sm" />
            <span className="text-slate-600 text-sm ml-2">— The Proof is in the Detail</span>
          </div>
          <div className="flex items-center gap-6 flex-wrap justify-center">
            <Link to="/guides" className="text-slate-500 hover:text-slate-700 text-sm">Guides</Link>
            <Link to="/pricing" className="text-slate-500 hover:text-slate-700 text-sm">Pricing</Link>
            <a href="https://symbionic.com.au" target="_blank" rel="noopener noreferrer" className="text-slate-500 hover:text-slate-700 text-sm">Symbionic</a>
            <Link to="/terms" className="text-slate-500 hover:text-slate-700 text-sm">Terms of Service</Link>
            <Link to="/privacy" className="text-slate-500 hover:text-slate-700 text-sm">Privacy Policy</Link>
            <p className="text-slate-500 text-sm">© 2026 VerifAEye. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {!isAuthenticated && <MobileCtaBar label="Start free — see the detail first" />}
    </div>
  );
};

export default Pricing;
